"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.inflateEnds = inflateEnds;
exports.linearRingIsClockwise = linearRingIsClockwise;
exports.linearRingsAreOriented = linearRingsAreOriented;
exports.linearRingssAreOriented = linearRingssAreOriented;
exports.orientLinearRings = orientLinearRings;
exports.orientLinearRingsArray = orientLinearRingsArray;

var _reverse = require("./reverse.js");

/**
 * @module ol/geom/flat/orient
 */

/**
 * Is the linear ring oriented clockwise in a coordinate system with a bottom-left
 * coordinate origin? For a coordinate system with a top-left coordinate origin,
 * the ring's orientation is clockwise when this function returns false.
 * @param {Array<number>} flatCoordinates Flat coordinates.
 * @param {number} offset Offset.
 * @param {number} end End.
 * @param {number} stride Stride.
 * @return {boolean} Is clockwise.
 */
function linearRingIsClockwise(flatCoordinates, offset, end, stride) {
  // https://stackoverflow.com/q/1165647/clockwise-method#1165943
  // https://github.com/OSGeo/gdal/blob/master/gdal/ogr/ogrlinearring.cpp
  var edge = 0;
  var x1 = flatCoordinates[end - stride];
  var y1 = flatCoordinates[end - stride + 1];

  for (; offset < end; offset += stride) {
    var x2 = flatCoordinates[offset];
    var y2 = flatCoordinates[offset + 1];
    edge += (x2 - x1) * (y2 + y1);
    x1 = x2;
    y1 = y2;
  }

  return edge === 0 ? undefined : edge > 0;
}
/**
 * Determines if linear rings are oriented.  By default, left-hand orientation
 * is tested (first ring must be clockwise, remaining rings counter-clockwise).
 * To test for right-hand orientation, use the `opt_right` argument.
 *
 * @param {Array<number>} flatCoordinates Flat coordinates.
 * @param {number} offset Offset.
 * @param {Array<number>} ends Array of end indexes.
 * @param {number} stride Stride.
 * @param {boolean} [opt_right] Test for right-hand orientation
 *     (counter-clockwise exterior ring and clockwise interior rings).
 * @return {boolean} Rings are correctly oriented.
 */


function linearRingsAreOriented(flatCoordinates, offset, ends, stride, opt_right) {
  var right = opt_right !== undefined ? opt_right : false;

  for (var i = 0, ii = ends.length; i < ii; ++i) {
    var end = ends[i];
    var isClockwise = linearRingIsClockwise(flatCoordinates, offset, end, stride);

    if (i === 0) {
      if (right && isClockwise || !right && !isClockwise) {
        return false;
      }
    } else {
      if (right && !isClockwise || !right && isClockwise) {
        return false;
      }
    }

    offset = end;
  }

  return true;
}
/**
 * Determines if linear rings are oriented.  By default, left-hand orientation
 * is tested (first ring must be clockwise, remaining rings counter-clockwise).
 * To test for right-hand orientation, use the `opt_right` argument.
 *
 * @param {Array<number>} flatCoordinates Flat coordinates.
 * @param {number} offset Offset.
 * @param {Array<Array<number>>} endss Array of array of end indexes.
 * @param {number} stride Stride.
 * @param {boolean} [opt_right] Test for right-hand orientation
 *     (counter-clockwise exterior ring and clockwise interior rings).
 * @return {boolean} Rings are correctly oriented.
 */


function linearRingssAreOriented(flatCoordinates, offset, endss, stride, opt_right) {
  for (var i = 0, ii = endss.length; i < ii; ++i) {
    var ends = endss[i];

    if (!linearRingsAreOriented(flatCoordinates, offset, ends, stride, opt_right)) {
      return false;
    }

    if (ends.length) {
      offset = ends[ends.length - 1];
    }
  }

  return true;
}
/**
 * Orient coordinates in a flat array of linear rings.  By default, rings
 * are oriented following the left-hand rule (clockwise for exterior and
 * counter-clockwise for interior rings).  To orient according to the
 * right-hand rule, use the `opt_right` argument.
 *
 * @param {Array<number>} flatCoordinates Flat coordinates.
 * @param {number} offset Offset.
 * @param {Array<number>} ends Ends.
 * @param {number} stride Stride.
 * @param {boolean} [opt_right] Follow the right-hand rule for orientation.
 * @return {number} End.
 */


function orientLinearRings(flatCoordinates, offset, ends, stride, opt_right) {
  var right = opt_right !== undefined ? opt_right : false;

  for (var i = 0, ii = ends.length; i < ii; ++i) {
    var end = ends[i];
    var isClockwise = linearRingIsClockwise(flatCoordinates, offset, end, stride);
    var reverse = i === 0 ? right && isClockwise || !right && !isClockwise : right && !isClockwise || !right && isClockwise;

    if (reverse) {
      (0, _reverse.coordinates)(flatCoordinates, offset, end, stride);
    }

    offset = end;
  }

  return offset;
}
/**
 * Orient coordinates in a flat array of linear rings.  By default, rings
 * are oriented following the left-hand rule (clockwise for exterior and
 * counter-clockwise for interior rings).  To orient according to the
 * right-hand rule, use the `opt_right` argument.
 *
 * @param {Array<number>} flatCoordinates Flat coordinates.
 * @param {number} offset Offset.
 * @param {Array<Array<number>>} endss Array of array of end indexes.
 * @param {number} stride Stride.
 * @param {boolean} [opt_right] Follow the right-hand rule for orientation.
 * @return {number} End.
 */


function orientLinearRingsArray(flatCoordinates, offset, endss, stride, opt_right) {
  for (var i = 0, ii = endss.length; i < ii; ++i) {
    offset = orientLinearRings(flatCoordinates, offset, endss[i], stride, opt_right);
  }

  return offset;
}
/**
 * Return a two-dimensional endss
 * @param {Array<number>} flatCoordinates Flat coordinates
 * @param {Array<number>} ends Linear ring end indexes
 * @return {Array<Array<number>>} Two dimensional endss array that can
 * be used to contruct a MultiPolygon
 */


function inflateEnds(flatCoordinates, ends) {
  var endss = [];
  var offset = 0;
  var prevEndIndex = 0;

  for (var i = 0, ii = ends.length; i < ii; ++i) {
    var end = ends[i]; // classifies an array of rings into polygons with outer rings and holes

    if (!linearRingIsClockwise(flatCoordinates, offset, end, 2)) {
      endss.push(ends.slice(prevEndIndex, i + 1));
    } else {
      if (endss.length === 0) {
        continue;
      }

      endss[endss.length - 1].push(ends[prevEndIndex]);
    }

    prevEndIndex = i + 1;
    offset = end;
  }

  return endss;
}
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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